import type { Data, Layout, Shape } from 'plotly.js-dist-min'
import type {
  DailyUsageBucketResponse,
  SourceUsageResponse,
  UsageAnalyticsResponse,
} from '@/lib/api'
import type { ChartPalette } from './palette'
import { formatDayLabels, isWeekend, isoDay } from './range'

export type ChartFigure = {
  data: Data[]
  layout: Partial<Layout>
}

/** One source-type swatch for the treemap's HTML legend (Plotly treemaps have
 * no native legend, so the page renders this list of category colours). */
export type TreemapLegendEntry = { label: string; color: string }

export type TreemapFigure = ChartFigure & { legend: TreemapLegendEntry[] }

export type DailyMetric = 'conversations' | 'turns' | 'uniqueUsers' | 'medianTurnsPerUser'

/** One day's thumbs-up/down tally (structural — matches the feedback hook's
 * point so lib/ needn't import from hooks/). */
export type FeedbackDailyDatum = { day: string; positive: number; negative: number }

const BASE_LAYOUT: Partial<Layout> = {
  margin: { l: 48, r: 16, t: 8, b: 40 },
  autosize: true,
  paper_bgcolor: 'rgba(0,0,0,0)',
  plot_bgcolor: 'rgba(0,0,0,0)',
  font: { family: 'Roboto, sans-serif', size: 12 },
}

function axisStyle(palette: ChartPalette): Partial<Layout['xaxis']> {
  return { gridcolor: palette.grid, zerolinecolor: palette.grid, color: palette.text }
}

function days(daily: DailyUsageBucketResponse[]): string[] {
  return daily.map((bucket) => bucket.day)
}

/** Insert zero buckets for every calendar day in [from, to] that has no
 * activity, so the daily charts render a continuous timeline rather than
 * skipping silent days (e.g. jumping 19.06 → 29.06). from/to are inclusive
 * ISO days (UTC). */
export function fillDailyGaps(
  daily: DailyUsageBucketResponse[],
  from: string,
  to: string,
): DailyUsageBucketResponse[] {
  const byDay = new Map(daily.map((bucket) => [bucket.day, bucket]))
  const filled: DailyUsageBucketResponse[] = []
  const cursor = new Date(from)
  const end = new Date(to)
  while (cursor <= end) {
    const day = isoDay(cursor)
    filled.push(
      byDay.get(day) ?? {
        day,
        conversations: 0,
        turns: 0,
        uniqueUsers: 0,
        medianTurnsPerUser: 0,
        users: [],
      },
    )
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return filled
}

/** Compact tick labels (year dropped under a one-year span). */
function dayLabels(daily: DailyUsageBucketResponse[]): string[] {
  return formatDayLabels(days(daily))
}

/** Shaded background bands behind Saturday/Sunday columns, so weekend dips
 * read as weekly rhythm rather than unexplained drops. Consecutive weekend
 * days merge into one band. The daily x-axes are `type: 'category'`, where
 * numeric shape coordinates are category indices — ±0.5 spans a full column. */
export function weekendBandShapes(days: string[], color: string): Array<Partial<Shape>> {
  const shapes: Array<Partial<Shape>> = []
  let bandStart: number | null = null
  for (let index = 0; index <= days.length; index++) {
    const day = days[index]
    const weekend = day !== undefined && isWeekend(day)
    if (weekend && bandStart === null) {
      bandStart = index
    } else if (!weekend && bandStart !== null) {
      shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: bandStart - 0.5,
        x1: index - 0.5,
        y0: 0,
        y1: 1,
        fillcolor: color,
        line: { width: 0 },
        layer: 'below',
      })
      bandStart = null
    }
  }
  return shapes
}

/** Anonymised users are labelled "Bruker N" upstream; the chart re-labels
 * them with letters (A, B, C, … then AA, AB, …) so a legend entry can never
 * be mistaken for the hover's conversation count. Letters are assigned in
 * upstream-number order, which keeps the legend sorted. */
function userLetter(index: number): string {
  let letter = ''
  let rest = index
  do {
    letter = String.fromCharCode(65 + (rest % 26)) + letter
    rest = Math.floor(rest / 26) - 1
  } while (rest >= 0)
  return letter
}

/** Upstream number in "Bruker N"; labels without one sort last, by name. */
function userSortKey(userLabel: string): number {
  const match = /\d+/.exec(userLabel)
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER
}

/** Word-wrap with literal <br> breaks. Plotly treemaps don't reflow long text
 * on their own (plotly.js#382) — they truncate to one line — so we insert the
 * breaks ourselves to let labels use a tile's full height. */
function wrapLabel(text: string, maxChars = 14): string {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    if (line === '') {
      line = word
    } else if (`${line} ${word}`.length <= maxChars) {
      line = `${line} ${word}`
    } else {
      lines.push(line)
      line = word
    }
  }
  if (line !== '') lines.push(line)
  return lines.join('<br>')
}

/** Stacked bar: one segment per anonymised user, so each day's height is the
 * total conversations and the split shows who drove them. */
export function buildConversationsByPersonFigure(
  daily: DailyUsageBucketResponse[],
  palette: ChartPalette,
): ChartFigure {
  const seen = new Set<string>()
  for (const bucket of daily) {
    for (const user of bucket.users) {
      seen.add(user.userLabel)
    }
  }
  const labels = [...seen].sort((a, b) => userSortKey(a) - userSortKey(b) || a.localeCompare(b))
  const x = dayLabels(daily)

  const data: Data[] = labels.map((label, index) => {
    const letter = userLetter(index)
    return {
      type: 'bar',
      name: letter,
      x,
      y: daily.map((bucket) => bucket.users.find((u) => u.userLabel === label)?.conversations ?? 0),
      marker: { color: palette.categorical[index % palette.categorical.length] ?? palette.accent },
      // The day is already on the x-axis, but the user must be repeated in the
      // hover — a bare count next to a bare legend letter is ambiguous.
      hovertemplate: `Bruker ${letter} · %{y} samtaler<extra></extra>`,
    }
  })

  return {
    data,
    layout: {
      ...BASE_LAYOUT,
      barmode: 'stack',
      xaxis: { ...axisStyle(palette), type: 'category', showgrid: false },
      yaxis: { ...axisStyle(palette), title: { text: 'Samtaler' }, rangemode: 'tozero' },
      shapes: weekendBandShapes(days(daily), palette.weekend),
      legend: { orientation: 'h', y: -0.2, title: { text: 'Anonymiserte brukere' } },
      showlegend: true,
    },
  }
}

const METRIC_COLOR: Record<DailyMetric, (p: ChartPalette) => string> = {
  conversations: (p) => p.conversations,
  turns: (p) => p.turns,
  uniqueUsers: (p) => p.users,
  medianTurnsPerUser: (p) => p.median,
}

/** Single line over the daily totals for one metric. */
export function buildDailyLineFigure(
  daily: DailyUsageBucketResponse[],
  metric: DailyMetric,
  yTitle: string,
  palette: ChartPalette,
): ChartFigure {
  const color = METRIC_COLOR[metric](palette)
  return {
    data: [
      {
        type: 'scatter',
        mode: 'lines+markers',
        x: dayLabels(daily),
        y: daily.map((bucket) => bucket[metric]),
        line: { color, width: 2 },
        marker: { color, size: 5 },
        hovertemplate: `%{x}<br>${yTitle}: %{y}<extra></extra>`,
      },
    ],
    layout: {
      ...BASE_LAYOUT,
      xaxis: { ...axisStyle(palette), type: 'category', showgrid: false },
      yaxis: { ...axisStyle(palette), title: { text: yTitle }, rangemode: 'tozero' },
      shapes: weekendBandShapes(days(daily), palette.weekend),
      showlegend: false,
    },
  }
}

/** Rank-frequency (Pareto) curve: every source sorted by citation count, so the
 * steep head of heavily-used sources and the long unused tail are both visible. */
export function buildSourceParetoFigure(
  sources: SourceUsageResponse[],
  palette: ChartPalette,
): ChartFigure {
  const sorted = [...sources].sort((a, b) => b.citations - a.citations)
  return {
    data: [
      {
        type: 'scatter',
        mode: 'lines',
        x: sorted.map((_, index) => index + 1),
        y: sorted.map((source) => source.citations),
        line: { color: palette.accent, width: 2 },
        fill: 'tozeroy',
        fillcolor: 'rgba(0, 56, 72, 0.08)',
        text: sorted.map((source) => source.docTitle),
        hovertemplate: '#%{x} · %{text}<br>%{y} siteringer<extra></extra>',
      },
    ],
    layout: {
      ...BASE_LAYOUT,
      xaxis: {
        ...axisStyle(palette),
        title: { text: 'Kilde (rangert etter bruk)' },
        showgrid: false,
      },
      yaxis: { ...axisStyle(palette), title: { text: 'Siteringer' }, rangemode: 'tozero' },
      showlegend: false,
    },
  }
}

/** Treemap grouped by source_type. Used sources are individual leaves sized by
 * citation count. Unused (never-cited) sources are hidden by default — there
 * are typically thousands of them and, collapsed into one count-sized "Ubrukt"
 * leaf per type, they dominate the area and crowd out the used sources you
 * actually want to read. Pass `includeUnused` to bring that leaf back. A type
 * with no visible sources is dropped rather than rendered as an empty tile. */
export function buildSourceTreemapFigure(
  sources: SourceUsageResponse[],
  palette: ChartPalette,
  includeUnused = false,
): TreemapFigure {
  const ids: string[] = []
  const labels: string[] = []
  const parents: string[] = []
  const values: number[] = []
  const colors: string[] = []
  // Per-node border widths: thick around the source-type groups, thin between
  // the individual sources inside a group.
  const lineWidths: number[] = []
  const GROUP_BORDER = 5
  const LEAF_BORDER = 1
  const legend: TreemapLegendEntry[] = []

  const byType = new Map<string, SourceUsageResponse[]>()
  for (const source of sources) {
    const type = source.sourceType || 'ukjent'
    const list = byType.get(type) ?? []
    list.push(source)
    byType.set(type, list)
  }

  let typeIndex = 0
  for (const [type, list] of byType) {
    const typeColor = palette.categorical[typeIndex % palette.categorical.length] ?? palette.accent
    typeIndex += 1

    const used = list.filter((source) => source.citations > 0)
    const unused = list.length - used.length
    if (used.length === 0 && (!includeUnused || unused === 0)) continue

    legend.push({ label: type, color: typeColor })
    ids.push(type)
    labels.push(type)
    parents.push('')
    values.push(0)
    colors.push(typeColor)
    lineWidths.push(GROUP_BORDER)

    for (const source of used) {
      ids.push(`${type}/${source.docUrl}`)
      labels.push(source.docTitle || source.docUrl)
      parents.push(type)
      values.push(source.citations)
      colors.push(typeColor)
      lineWidths.push(LEAF_BORDER)
    }
    if (includeUnused && unused > 0) {
      ids.push(`${type}/__unused`)
      labels.push(`Ubrukt · ${unused} kilder`)
      parents.push(type)
      values.push(unused)
      colors.push(palette.unused)
      lineWidths.push(LEAF_BORDER)
    }
  }

  return {
    data: [
      {
        type: 'treemap',
        ids,
        labels,
        parents,
        values,
        marker: {
          colors,
          // marker.line.color defaults to paper_bgcolor, which is transparent
          // here — so without setting it explicitly the tiles have no visible
          // separators. Use the card surface; per-node widths make the group
          // outlines thick and the inner source separators thin.
          line: { color: palette.surface, width: lineWidths },
          pad: { t: 1, l: 1, r: 1, b: 1 },
        },
        branchvalues: 'remainder',
        // Top-left, word-wrapped label (`text`, not `label`, so the hover keeps
        // the clean single-line title). The value lives in the hover.
        text: labels.map((label) => wrapLabel(label)),
        texttemplate: '%{text}',
        textposition: 'top left',
        textfont: { family: 'Roboto, sans-serif', size: 10 },
        hovertemplate: '%{label}<br>%{value}<extra></extra>',
        tiling: { pad: 1 },
      } as Data,
    ],
    // No uniformtext: forcing one shared font size blanks every tile that
    // can't fit text at that size. Letting Plotly fit each tile's label
    // independently shows labels on far more rectangles (at varying sizes).
    layout: {
      ...BASE_LAYOUT,
      margin: { l: 8, r: 8, t: 8, b: 8 },
    },
    legend,
  }
}

/** Grouped bars per day: positive (success) vs negative (error) feedback.
 * Day is on the x-axis, so the hover shows the count only. */
export function buildFeedbackDailyFigure(
  points: FeedbackDailyDatum[],
  palette: ChartPalette,
): ChartFigure {
  const x = formatDayLabels(points.map((point) => point.day))
  return {
    data: [
      {
        type: 'bar',
        name: 'Positiv',
        x,
        y: points.map((point) => point.positive),
        marker: { color: palette.success },
        hovertemplate: '%{y}<extra></extra>',
      },
      {
        type: 'bar',
        name: 'Negativ',
        x,
        y: points.map((point) => point.negative),
        marker: { color: palette.error },
        hovertemplate: '%{y}<extra></extra>',
      },
    ],
    layout: {
      ...BASE_LAYOUT,
      barmode: 'group',
      xaxis: { ...axisStyle(palette), type: 'category', showgrid: false },
      yaxis: { ...axisStyle(palette), title: { text: 'Tilbakemeldinger' }, rangemode: 'tozero' },
      shapes: weekendBandShapes(
        points.map((point) => point.day),
        palette.weekend,
      ),
      legend: { orientation: 'h', y: -0.2 },
      showlegend: true,
    },
  }
}

export function hasDailyData(analytics: UsageAnalyticsResponse): boolean {
  return analytics.daily.length > 0
}
